import React, { useState } from 'react';
import { MorningApprovalData, DailyCheckpoint } from '../../../types/isometric.types';

interface MorningCheckInModalProps {
  isOpen: boolean;
  checkpoint: DailyCheckpoint | null;
  data: MorningApprovalData | null;
  timeRemaining: number; // seconds until auto-proceed
  onApprove: (comments?: string) => void;
  onAdjust: (adjustments: string[]) => void;
  onSkip: () => void;
  onClose: () => void;
}

export const MorningCheckInModal: React.FC<MorningCheckInModalProps> = ({
  isOpen,
  checkpoint: _checkpoint,
  data,
  timeRemaining,
  onApprove,
  onAdjust,
  onSkip,
  onClose: _onClose,
}) => {
  const [comments, setComments] = useState('');
  const [showAdjustments, setShowAdjustments] = useState(false);
  const [adjustments, setAdjustments] = useState<string[]>([]);
  const [newAdjustment, setNewAdjustment] = useState('');

  if (!isOpen || !data) return null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAddAdjustment = () => {
    if (newAdjustment.trim()) {
      setAdjustments([...adjustments, newAdjustment.trim()]);
      setNewAdjustment('');
    }
  };

  const handleRemoveAdjustment = (index: number) => {
    setAdjustments(adjustments.filter((_, i) => i !== index));
  };

  const handleApprove = () => {
    if (adjustments.length > 0) {
      onAdjust(adjustments);
    } else {
      onApprove(comments || undefined);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-xl shadow-2xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="text-3xl">☀️</span>
                Good Morning!
              </h2>
              <p className="text-blue-100 mt-1">
                Review and approve today's action plan
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-blue-200">Auto-proceed in</div>
              <div className="text-2xl font-mono font-bold text-white">
                {formatTime(timeRemaining)}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Yesterday's Summary */}
          {data.yesterdaySummary && (
            <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-2">
                Yesterday's Recap
              </h3>
              <p className="text-slate-300">{data.yesterdaySummary}</p>
            </div>
          )}

          {/* Carryover Tasks */}
          {data.carryoverTasks.length > 0 && (
            <div className="mb-6 p-4 bg-amber-900/20 rounded-lg border border-amber-700/50">
              <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wide mb-2">
                Carried Over from Yesterday
              </h3>
              <ul className="space-y-1">
                {data.carryoverTasks.map((task, i) => (
                  <li key={i} className="text-amber-200 flex items-center gap-2">
                    <span className="text-amber-500">⟳</span> {task}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Today's Plan */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span>📋</span> Today's Action Plan
            </h3>
            <div className="bg-slate-800 rounded-lg border border-slate-700">
              <div className="p-4 border-b border-slate-700">
                <h4 className="font-bold text-white">{data.todaysPlan.title}</h4>
                <p className="text-slate-400 text-sm mt-1">{data.todaysPlan.description}</p>
              </div>
              <ul className="divide-y divide-slate-700">
                {data.todaysPlan.actions.map((action, i) => (
                  <li key={action.id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          action.priority === 'CRITICAL'
                            ? 'bg-red-500/20 text-red-400'
                            : action.priority === 'HIGH'
                              ? 'bg-orange-500/20 text-orange-400'
                              : action.priority === 'MEDIUM'
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-slate-500/20 text-slate-400'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <div>
                        <span className="text-white">{action.title}</span>
                        <span
                          className={`ml-2 text-xs px-2 py-0.5 rounded ${
                            action.priority === 'CRITICAL'
                              ? 'bg-red-500/20 text-red-400'
                              : action.priority === 'HIGH'
                                ? 'bg-orange-500/20 text-orange-400'
                                : action.priority === 'MEDIUM'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-slate-500/20 text-slate-400'
                          }`}
                        >
                          {action.priority}
                        </span>
                      </div>
                    </div>
                    <span className="text-slate-500 text-sm">
                      ~{action.estimatedDuration} min
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Suggested Priorities */}
          {data.suggestedPriorities.length > 0 && (
            <div className="mb-6 p-4 bg-cyan-900/20 rounded-lg border border-cyan-700/50">
              <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wide mb-2">
                AI Suggestions
              </h3>
              <ul className="space-y-2">
                {data.suggestedPriorities.map((suggestion, i) => (
                  <li key={i} className="text-cyan-200 flex items-start gap-2">
                    <span className="text-cyan-500 mt-0.5">💡</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Comments */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-400 mb-2">
              Add Comments (Optional)
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Any specific focus areas or notes for today..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
            />
          </div>

          {/* Adjustments Section */}
          {showAdjustments && (
            <div className="mb-6 p-4 bg-slate-800 rounded-lg border border-slate-700">
              <h4 className="font-medium text-white mb-3">Plan Adjustments</h4>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newAdjustment}
                  onChange={(e) => setNewAdjustment(e.target.value)}
                  placeholder="Add a task or modification..."
                  className="flex-1 px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white placeholder-slate-500 text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAdjustment()}
                />
                <button
                  onClick={handleAddAdjustment}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                >
                  Add
                </button>
              </div>
              {adjustments.length > 0 && (
                <ul className="space-y-2">
                  {adjustments.map((adj, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between p-2 bg-slate-900 rounded"
                    >
                      <span className="text-slate-300 text-sm">{adj}</span>
                      <button
                        onClick={() => handleRemoveAdjustment(i)}
                        className="text-slate-500 hover:text-red-400"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 bg-slate-800/50 border-t border-slate-700 flex items-center justify-between">
          <div className="flex gap-3">
            <button
              onClick={onSkip}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Skip & Continue
            </button>
            <button
              onClick={() => setShowAdjustments(!showAdjustments)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                showAdjustments
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {showAdjustments ? 'Hide Adjustments' : 'Make Adjustments'}
            </button>
          </div>
          <button
            onClick={handleApprove}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg"
          >
            {adjustments.length > 0 ? 'Approve with Changes' : 'Approve Plan'} ✓
          </button>
        </div>
      </div>
    </div>
  );
};
